import { Localization } from "@viamap/viamap2-common";
import {
   historyCommentForProvenance,
   type ANNServitutProvenance,
} from "src/modules/ANNModule/ANNServitutProvenance";
import {
   saveUniqueFieldNote,
   type ServitutAnalysisFieldKey,
} from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import {
   AnnotationContextType,
   AnnotationType,
   type ANNAnnotationRecord,
   type SaveAnnotationInput,
} from "src/modules/ANNModule/ANNTypes";
import { SERVITUT_IGNORE_NOTE_COMMENT } from "../model/ignore";
import { rowDocumentId } from "../model/identity";
import { mergeAnalysisItems } from "../model/projectAnn";
import { isServitutPdfDocumentAnnotation } from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import { normalizeAktPages } from "../model/pages";
import { isDigitalRow, isNativeDigitalDocument, isUserFileDocument, lookedAtAktId, lookedAtAktSource, type ServitutRow } from "../model/row";

export type CommitSession = {
   organizationId: string;
   userId: string;
   userName: string;
};

export function provenanceFromRow(row: ServitutRow): ANNServitutProvenance {
   const fromFile = row.document.kind === "upload"
      || (row.document.kind === "annotation" && row.document.origin === "upload");
   const aktId = fromFile ? undefined : lookedAtAktId(row);
   const usedSource = fromFile ? undefined : lookedAtAktSource(row);
   return {
      reviewState: row.review.state,
      origin: row.review.origin,
      source: fromFile ? "file" : "akt",
      ...(aktId ? { aktId } : {}),
      ...(usedSource ? { usedSource } : {}),
      aiPagesKept: row.pages.aiSuggested,
      analysisStale: Boolean(row.analysis?.stale),
      fieldStatus: {
         pages: row.review.state === "checked" ? "person_approved" : undefined,
         resume: row.fields.resume.aiStatus,
         entitledParty: row.fields.entitledParty.aiStatus,
         condition: row.fields.condition.aiStatus,
         statement: row.fields.statement.aiStatus,
      },
   };
}

export function buildPageDocumentSaveInput(
   row: ServitutRow,
   session: CommitSession,
   document: {
      s3Key: string;
      filename?: string;
      s3Url?: string;
      mimeType?: string;
      size?: number;
      existing?: ANNAnnotationRecord;
   },
): SaveAnnotationInput {
   const pages = normalizeAktPages(row.pages.selected.length ? row.pages.selected : row.pages.aiSuggested);
   const provenance = provenanceFromRow(row);
   const existing = document.existing;
   const filename = provenance.source === "file"
      ? document.filename
      : (lookedAtAktId(row) || document.filename);
   const liveAnalysis = row.analysis
      ? {
         analyzedAt: new Date().toISOString(),
         caseDescription: row.analysis.item.case_description ?? existing?.document?.llmAnalysis?.caseDescription ?? null,
         item: mergeAnalysisItems(row.analysis.item, existing?.document?.llmAnalysis?.item) || row.analysis.item,
      }
      : existing?.document?.llmAnalysis;
   return {
      type: AnnotationType.Document,
      documentId: existing?.documentId || rowDocumentId(row),
      contextType: AnnotationContextType.Servitut,
      organizationId: session.organizationId,
      userId: session.userId,
      userName: session.userName,
      datoloebenr: existing?.datoloebenr || row.identity.datoloebenr,
      document: {
         filename,
         s3Key: document.s3Key,
         s3Url: document.s3Url,
         mimeType: document.mimeType || "application/pdf",
         size: document.size,
         llmAnalysis: liveAnalysis,
         provenance,
         ...(pages.length && row.pages.extent !== "entire_akt" ? { extractedPages: pages } : {}),
      },
      title: (filename || existing?.title || row.landRegistry.displayAktId || row.id).replace(/\.pdf$/i, ""),
      historyComment: [
         pages.length
            ? Localization.getFormattedText("Saved from servitutredegørelse. Pages: {pages}", { pages: pages.join(", ") })
            : Localization.getText("Saved from servitutredegørelse."),
         historyCommentForProvenance(provenance),
      ].filter(Boolean).join(" · "),
      verified: true,
      annotationType: existing?.annotationType || "Dokument",
      id: existing?.id,
      originalCreatedAt: existing?.createdAt,
      originalCreatedBy: existing?.createdBy,
      originalCreatedByName: existing?.createdByName,
   };
}

export function canCommitPagesWithoutPdfFetch(row: ServitutRow, existing?: ANNAnnotationRecord): boolean {
   if (row.document.kind === "annotation" && Boolean(row.document.s3Key)) {
      return true;
   }
   return isDigitalRow(row) && row.document.kind !== "upload" && Boolean(existing?.document?.s3Key);
}

/** Native digital packages are the whole servitut; accepting them must not store a PDF copy. */
export function shouldPersistPageDocument(row: ServitutRow): boolean {
   return !isNativeDigitalDocument(row);
}

export function isRedundantNativeDigitalDocument(row: ServitutRow, record: ANNAnnotationRecord): boolean {
   if (!isDigitalRow(row) || isUserFileDocument(row) || record.document?.provenance?.source === "file") {
      return false;
   }
   return isServitutPdfDocumentAnnotation(record);
}

export async function dropRedundantNativeDigitalDocuments(params: {
   row: ServitutRow;
   records: ANNAnnotationRecord[];
   organizationId?: string;
   deleteAnnotation: (id: string, organizationId?: string) => Promise<void>;
}): Promise<ANNAnnotationRecord[]> {
   const kept: ANNAnnotationRecord[] = [];
   for (const record of params.records) {
      if (!isRedundantNativeDigitalDocument(params.row, record) || !record.id) {
         kept.push(record);
         continue;
      }
      try {
         await params.deleteAnnotation(record.id, params.organizationId);
      } catch (error) {
         console.warn("[Servitutredegørelse] failed to drop redundant digital document annotation", params.row.id, record.id, error);
         kept.push(record);
      }
   }
   return kept;
}

export function buildIgnoreNoteSaveInput(
   row: ServitutRow,
   session: CommitSession,
   existing?: ANNAnnotationRecord,
): SaveAnnotationInput {
   return {
      type: AnnotationType.Note,
      documentId: existing?.documentId || rowDocumentId(row),
      contextType: AnnotationContextType.Servitut,
      organizationId: session.organizationId,
      userId: session.userId,
      userName: session.userName,
      datoloebenr: existing?.datoloebenr || row.identity.datoloebenr,
      note: { comment: SERVITUT_IGNORE_NOTE_COMMENT, text: "ignored" },
      title: Localization.getText("Ignored"),
      historyComment: Localization.getText("Ignored from servitutredegørelse"),
      verified: true,
      annotationType: "Notat",
      id: existing?.id,
      originalCreatedAt: existing?.createdAt,
      originalCreatedBy: existing?.createdBy,
      originalCreatedByName: existing?.createdByName,
   };
}

export async function commitFieldNote(params: {
   row: ServitutRow;
   field: ServitutAnalysisFieldKey;
   text: string;
   session: CommitSession;
   existing?: ANNAnnotationRecord;
   siblings?: ANNAnnotationRecord[];
   saveAnnotation: (input: SaveAnnotationInput) => Promise<ANNAnnotationRecord>;
   deleteAnnotation: (id: string, organizationId?: string) => Promise<void>;
}): Promise<ANNAnnotationRecord> {
   return saveUniqueFieldNote({
      field: params.field,
      documentId: rowDocumentId(params.row),
      datoLoebenr: params.row.identity.datoloebenr,
      organizationId: params.session.organizationId,
      userId: params.session.userId,
      userName: params.session.userName,
      text: params.text,
      existing: params.existing,
      siblings: params.siblings,
      verified: true,
      historyComment: Localization.getText("Accepted from servitutredegørelse"),
      paataleberettigetResolution: params.field === "entitledParty"
         ? params.row.analysis?.item.paataleberettiget_resolution
         : undefined,
      saveAnnotation: params.saveAnnotation,
      deleteAnnotation: params.deleteAnnotation,
   });
}
